import React, { useState } from 'react';
import {
  ChevronDown,
  AlertCircle,
  Loader2,
  CreditCard,
  Settings,
  RotateCcw,
  Gift,
  Minus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  CreditTransactionType,
  TransactionListResponse,
  TRANSACTION_TYPE_LABELS,
  CreditService,
} from '@aaas/shared';

interface TransactionHistoryProps {
  transactions: TransactionListResponse | null;
  loading: boolean;
  error: string | null;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  loading,
  error,
  onLoadMore,
  canLoadMore = false,
}) => {
  const { t } = useTranslation('portal');
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());

  const toggleExpanded = (transactionId: string) => {
    const newExpanded = new Set(expandedTransactions);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedTransactions(newExpanded);
  };

  const getTransactionIcon = (type: CreditTransactionType) => {
    const iconMap = {
      purchase: CreditCard,
      usage: Minus,
      refund: RotateCcw,
      bonus: Gift,
      adjustment: Settings,
    };

    return iconMap[type] || CreditCard;
  };

  const formatTransactionDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInMs / (1000 * 60));
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const formatTransactionTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !transactions) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('transactions.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6" />
          <div>
            <div className="font-medium">{t('transactions.loadFailed')}</div>
            <div className="text-sm text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!transactions || transactions.transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="font-medium">{t('transactions.noTransactionsHeading')}</div>
          <div className="text-sm">{t('transactions.noTransactionsText')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{t('transactions.heading')}</h3>
        <div className="text-sm text-gray-500">
          {t('transactions.totalCount', { count: transactions.total_count })}
        </div>
      </div>

      {/* Transactions List */}
      <div className="space-y-2">
        {transactions.transactions.map((transaction) => {
          const Icon = getTransactionIcon(transaction.transaction_type);
          const isExpanded = expandedTransactions.has(transaction.id);
          const isDeduction = CreditService.isDeduction(transaction.transaction_type);
          const amountColor = CreditService.getTransactionTypeColor(transaction.transaction_type);

          return (
            <div
              key={transaction.id}
              className="border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              {/* Main Transaction Row */}
              <div className="p-4 cursor-pointer" onClick={() => toggleExpanded(transaction.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-lg ${isDeduction ? 'bg-red-100' : 'bg-green-100'}`}
                    >
                      <Icon
                        className={`w-4 h-4 ${isDeduction ? 'text-red-600' : 'text-green-600'}`}
                      />
                    </div>

                    <div>
                      <div className="font-medium text-gray-900">
                        {TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTransactionDate(transaction.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className={`font-semibold ${amountColor}`}>
                        {CreditService.formatTransactionAmount(
                          transaction.credits_change,
                          transaction.transaction_type
                        )}
                      </div>
                    </div>

                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-medium text-gray-700">{t('transactions.transactionId')}</div>
                        <div className="text-gray-600 font-mono text-xs">{transaction.id}</div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-700">{t('transactions.dateTime')}</div>
                        <div className="text-gray-600">
                          {formatTransactionTime(transaction.created_at)}
                        </div>
                      </div>
                    </div>

                    {transaction.transaction_metadata &&
                      Object.keys(transaction.transaction_metadata).length > 0 && (
                        <div>
                          <div className="font-medium text-gray-700 text-sm">{t('transactions.details')}</div>
                          <div className="text-gray-600 text-sm">
                            <pre className="whitespace-pre-wrap">
                              {JSON.stringify(transaction.transaction_metadata, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}

                    {transaction.payment_order_id && (
                      <div>
                        <div className="font-medium text-gray-700 text-sm">{t('transactions.paymentDetails')}</div>
                        <div className="text-gray-600 text-sm">
                          Payment Order ID: {transaction.payment_order_id}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {canLoadMore && (
        <div className="text-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>{loading ? t('transactions.loadingMore') : t('transactions.loadMore')}</span>
          </button>
        </div>
      )}

      {/* Pagination Info */}
      {transactions && (
        <div className="text-center text-sm text-gray-500 pt-2">
          {t('transactions.showingInfo', {
            shown: transactions.transactions.length,
            total: transactions.total_count,
          })}
        </div>
      )}
    </div>
  );
};
